/* SHURI: kinetic typography, viewport entrances and scroll-linked photography. */
(() => {
  'use strict';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const canAnimate = typeof Element.prototype.animate === 'function';
  const animations = new Set();
  const ease = 'cubic-bezier(.16,1,.3,1)';
  const hero = document.querySelector('.hero');
  const root = document.documentElement;
  let pageReady = false;
  let prepared = false;
  let allowMotion = true;

  function animate(element, frames, duration = 460, delay = 0, easing = ease) {
    if (!pageReady || !allowMotion || reduced.matches || !canAnimate || document.hidden) return;
    const animation = element.animate(frames, { duration, delay, easing, fill: 'backwards' });
    animations.add(animation);
    const done = () => { animations.delete(animation); };
    animation.finished.then(done, done);
    return animation;
  }

  function kineticLines(element, hero = false) {
    const vertical = getComputedStyle(element.querySelector('.kinetic-line') || element).writingMode.startsWith('vertical');
    const lines = [...element.querySelectorAll('.kinetic-line')];
    const step = Math.min(35, 150 / Math.max(1, lines.length - 1));
    lines.forEach((line, index) => animate(line, [
      { opacity: 0, transform: vertical ? 'translate3d(14px,0,0)' : 'translate3d(0,12px,0)' },
      { opacity: 1, transform: 'translate3d(0,0,0)' }
    ], 420, (hero ? 180 : 0) + step * index));
  }

  function kinetic(element, hero = false) {
    const glyphs = [...element.querySelectorAll('.kinetic-glyph')];
    const vertical = getComputedStyle(element).writingMode.startsWith('vertical');
    const step = Math.min(hero ? 22 : 18, (hero ? 220 : 160) / Math.max(1, glyphs.length - 1));
    glyphs.forEach((glyph, index) => animate(glyph, [
      { opacity: 0, transform: vertical ? 'translate3d(.65em,-.2em,0)' : 'translate3d(0,.75em,0)' },
      { opacity: 1, transform: 'translate3d(0,0,0)' }
    ], hero ? 500 : 440, (hero ? 100 : 0) + step * index));
  }

  let heroEntered = false;
  function enterHeroOnce() {
    if (heroEntered || !pageReady || document.hidden) return;
    heroEntered = true;
    hero.dataset.entryState = 'shown';
    if (hero.getBoundingClientRect().bottom <= 0) {
      root.classList.remove('shuri-copy-pending');
      return;
    }
    if (allowMotion && canAnimate && !reduced.matches) {
      hero.classList.add('has-zoom');
      hero.dataset.zoomState = 'playing';
      const zoom = animate(hero.querySelector('.hero-photo img'), [
        { transform: 'scale(1)' }, { transform: 'scale(1.1)' }
      ], 8000, 0, 'linear');
      const finish = () => { hero.dataset.zoomState = 'complete'; };
      zoom.finished.then(finish, finish);
    }
    kinetic(hero.querySelector('.hero-title'), true);
    kineticLines(hero.querySelector('.hero-desc'), true);
    // Reveal only after backwards-filled animations own every glyph and line.
    root.classList.remove('shuri-copy-pending');
  }

  const entries = new Map();
  const register = (selector, kind) => document.querySelectorAll(selector).forEach(element => entries.set(element, kind));
  register('.chapter-caption>span, .chapter-label>span, .horizontal-title, .collage-copy h3, .spec h3', 'type');
  register('.checker .photo, .product-card, .collage .photo, .spec-layout>.photo', 'photo');
  register('.intro-copy, .side-copy', 'lines');
  register('.checker-copy p, .collage-copy p, .spec dl>div, main .button-row, .checker-copy .button, .footer-col, .footer-nav', 'rise');

  function enter(element, kind, delay = 0) {
    element.dataset.entryState = 'shown';
    if (kind === 'type') { kinetic(element); return; }
    if (kind === 'lines') { kineticLines(element); return; }
    if (kind === 'photo') {
      animate(element, [
        { opacity: 0, transform: 'translate3d(0,20px,0)' },
        { opacity: 1, transform: 'translate3d(0,0,0)' }
      ], 520, delay);
      return;
    }
    animate(element, [
      { opacity: 0, transform: 'translate3d(0,14px,0)' },
      { opacity: 1, transform: 'translate3d(0,0,0)' }
    ], 420, delay);
  }

  let entranceObserver;
  function startEntrances() {
    if (!allowMotion || !canAnimate || !('IntersectionObserver' in window) || reduced.matches) return;
    entranceObserver = new IntersectionObserver(records => {
      const visible = records.filter(record => record.isIntersecting);
      visible.forEach((record, index) => {
        enter(record.target, entries.get(record.target), Math.min(index * 35, 105));
        entranceObserver.unobserve(record.target);
      });
    }, { threshold: 0, rootMargin: '0px 0px -5% 0px' });
    entries.forEach((kind, element) => {
      element.dataset.entryState = 'ready';
      entranceObserver.observe(element);
    });
  }

  const banners = [...document.querySelectorAll('.chapter-visual')].map(element => ({
    element, photo: element.querySelector(':scope>.photo'), top: 0, height: 0, range: 0, visible: true
  }));
  let frame = 0;
  let viewportHeight = innerHeight;

  function renderParallax() {
    frame = 0;
    if (reduced.matches || document.hidden) return;
    const scroll = scrollY;
    // All geometry was measured together; scrolling only writes transforms.
    for (const banner of banners) {
      if (!banner.visible) continue;
      const center = banner.top - scroll + banner.height / 2;
      const progress = Math.max(-1, Math.min(1, (viewportHeight / 2 - center) / ((viewportHeight + banner.height) / 2)));
      banner.photo.style.transform = `translate3d(0,${(progress * banner.range).toFixed(2)}px,0)`;
    }
  }

  function scheduleParallax() {
    if (!frame && !reduced.matches && !document.hidden) frame = requestAnimationFrame(renderParallax);
  }

  function measureParallax() {
    viewportHeight = innerHeight;
    banners.forEach(banner => {
      const rect = banner.element.getBoundingClientRect();
      banner.top = rect.top + scrollY;
      banner.height = rect.height;
      banner.range = parseFloat(getComputedStyle(banner.element).getPropertyValue('--parallax-range')) || 0;
    });
    scheduleParallax();
  }

  if ('IntersectionObserver' in window) {
    const visibilityObserver = new IntersectionObserver(records => {
      records.forEach(record => {
        const banner = banners.find(item => item.element === record.target);
        banner.visible = record.isIntersecting;
        banner.element.classList.toggle('parallax-visible', record.isIntersecting && !reduced.matches);
      });
      scheduleParallax();
    }, { rootMargin: '120px 0px' });
    banners.forEach(banner => visibilityObserver.observe(banner.element));
  }

  function syncPreference() {
    banners.forEach(banner => {
      banner.element.classList.toggle('has-parallax', !reduced.matches);
      banner.element.classList.toggle('parallax-visible', banner.visible && !reduced.matches);
      if (reduced.matches) banner.photo.style.removeProperty('transform');
    });
    if (reduced.matches) {
      animations.forEach(animation => animation.cancel());
      entranceObserver?.disconnect();
      entries.forEach((kind, element) => { element.dataset.entryState = 'shown'; });
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
    }
    measureParallax();
  }

  addEventListener('scroll', scheduleParallax, { passive: true });
  addEventListener('resize', measureParallax, { passive: true });
  addEventListener('pageshow', measureParallax);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) animations.forEach(animation => animation.cancel());
    else { measureParallax(); if (prepared) { revealPage(); requestHeroEntry(); } }
  });
  reduced.addEventListener('change', syncPreference);
  syncPreference();

  function requestHeroEntry() {
    if (window.RGCEntry) window.RGCEntry.ready.then(enterHeroOnce);
    else enterHeroOnce();
  }

  function revealPage(withMotion = true) {
    if (pageReady || document.hidden) return;
    pageReady = true;
    allowMotion = withMotion && !root.classList.contains('boot-fallback');
    const loader = document.querySelector('.loading-screen');
    loader?.classList.add('is-leaving');
    root.classList.remove('is-loading');
    root.classList.add('is-ready');
    window.dispatchEvent(new Event('shuri:ready'));
    measureParallax();
    requestHeroEntry();
    startEntrances();
    if (loader && canAnimate && allowMotion && !reduced.matches) {
      const fade = loader.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 300, easing: 'ease-out', fill: 'forwards' });
      fade.finished.then(() => loader.remove(), () => loader.remove());
    } else loader?.remove();
  }

  function imageReady(image) {
    if (typeof image.decode === 'function') return image.decode().catch(() => {});
    if (image.complete) return Promise.resolve();
    return new Promise(resolve => {
      image.addEventListener('load', resolve, { once: true });
      image.addEventListener('error', resolve, { once: true });
    });
  }

  window.addEventListener('shuri:boot-fallback', () => { prepared = true; revealPage(false); }, { once: true });
  if (root.classList.contains('boot-fallback')) { prepared = true; revealPage(false); }
  else Promise.all([
    ...[...document.querySelectorAll('.hero-photo img, .brand-photo img')].map(imageReady),
    document.fonts?.ready || Promise.resolve()
  ]).then(() => {
    prepared = true;
    requestAnimationFrame(() => requestAnimationFrame(() => revealPage()));
  });
})();
